import {
  Button as CKButton,
  paletteHelper,
  spacingHelper,
  typographyHelper,
  mediaHelper,
  type Button,
  radiusHelper,
} from '@property.com.au/ui-kit';
import type { ComponentProps, FunctionComponent } from 'react';
import styled from 'styled-components';
import { useTrackBeforeYouBuyModalEvent } from '../../../../hooks/useTrackBeforeYouBuyModalEvent';
import { usePropertyPageModalContext } from '../../../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import beforeYouBuyLogoSvg from '../../../../../assets/beforeyoubuy-logo.svg';
import {
  DynamicContents,
  DynamicContentsItem,
} from '../../../../../app-kit/components/DynamicContent/types';
import { DynamicContent } from '../../../../../app-kit/components/DynamicContent/DynamicContent';

const LogoButton = styled.button`
  align-self: flex-start;

  ${mediaHelper('tabletMin')} {
    align-self: auto;
  }
`;

const Logo = styled.img`
  width: 11.25rem;
  height: 2.1875rem;
`;

const StyledButton = styled(CKButton)`
  ${mediaHelper('tabletMin')} {
    order: -1;
  }

  /* Beforeyoubuy brand colour */
  background-color: rgb(9 0 52 / 100%);
  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    background-color: rgb(9 0 52 / 80%);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingHelper('small')};
  margin-bottom: ${spacingHelper('medium')};
`;

const Card = styled.div`
  background-color: ${paletteHelper('backgroundBlueSecondary')};

  /* GreenShadow */
  border-radius: ${radiusHelper('medium')};
  display: flex;
  flex-direction: column;
  padding: ${spacingHelper('large')};
  gap: ${spacingHelper('twoExtraSmall')};

  ${mediaHelper('mobile')} {
    padding: ${spacingHelper('medium')};
  }
`;

const Title = styled.h3`
  ${typographyHelper('body01Bold')};
`;
const SubTitle = styled.h4`
  ${mediaHelper('tabletMin')} {
    max-width: calc(${spacingHelper('extraLarge')} * 16);
  }

  ${typographyHelper('subtitle02')};
`;

const ButtonContainer = styled.div`
  margin-top: ${spacingHelper('medium')};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  row-gap: 1.25rem;

  ${mediaHelper('tabletMin')} {
    flex-direction: row;
  }
`;

const DynamicDisclaimer = styled.div`
  ${typographyHelper('caption')};

  span {
    ${typographyHelper('caption')};
    color: ${paletteHelper('textSecondary')};
  }
  a {
    ${typographyHelper('caption')};
  }
`;

interface PropertyTimelineBeforeYouBuyProps {
  title: string;
  subtitle: string;
  disclaimer: DynamicContents<DynamicContentsItem> | null;
  reportCTA: ComponentProps<typeof Button>;
}
export const PropertyTimelineBeforeYouBuy: FunctionComponent<PropertyTimelineBeforeYouBuyProps> = ({
  title,
  subtitle,
  disclaimer,
  reportCTA: { value, variant, icon, iconPlacement },
}) => {
  const { displayModal } = usePropertyPageModalContext();
  const trackBeforeYouBuyModalEvent = useTrackBeforeYouBuyModalEvent();

  return (
    <Container>
      <Card>
        <Title>{title}</Title>
        <SubTitle>{subtitle}</SubTitle>
        <ButtonContainer>
          <LogoButton
            type="button"
            onClick={() => {
              displayModal({ name: 'beforeYouBuyModal' });
              trackBeforeYouBuyModalEvent('property_page__byb_brick_logo_button');
            }}
          >
            <Logo loading="lazy" alt="Before You Buy" src={beforeYouBuyLogoSvg} />
          </LogoButton>
          <StyledButton
            sizeVariant="medium"
            variant={variant}
            icon={icon}
            iconPlacement={iconPlacement}
            onClick={() => {
              displayModal({ name: 'beforeYouBuyModal' });
              trackBeforeYouBuyModalEvent('property_page__byb_brick_cta_button');
            }}
          >
            {value}
          </StyledButton>
        </ButtonContainer>
      </Card>
      {disclaimer && (
        <DynamicDisclaimer>
          <DynamicContent dynamicContents={disclaimer} />
        </DynamicDisclaimer>
      )}
    </Container>
  );
};
